import { useCallback, useEffect, useState } from "react";
import { BadgePercent, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const STATUS = {
  live: { text: "Live now", cls: "bg-emerald-100 text-emerald-800" },
  scheduled: { text: "Scheduled", cls: "bg-sky-100 text-sky-800" },
  outside_hours: { text: "Off-hours", cls: "bg-amber-100 text-amber-800" },
  ended: { text: "Ended", cls: "bg-stone-100 text-stone-500" },
  off: { text: "Paused", cls: "bg-stone-100 text-stone-500" },
};
const field = "w-full px-3 py-2.5 rounded-xl border-2 border-stone-200 bg-white text-sm outline-none focus:border-brand-red";
const label = "text-[10px] uppercase tracking-widest text-stone-500 font-bold";
const toLocalInput = (iso) => (iso ? new Date(new Date(iso).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : "");
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);
const EMPTY = { name: "", percent: "20", starts_at: "", ends_at: "", days: [], start_time: "", end_time: "", channels: ["online", "pos"], active: true };

function PromoForm({ token, initial, onSaved, onClose }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setF(initial?.id ? { ...initial, percent: String(initial.percent), starts_at: toLocalInput(initial.starts_at), ends_at: toLocalInput(initial.ends_at), start_time: initial.start_time || "", end_time: initial.end_time || "" } : EMPTY);
  }, [initial]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const toggle = (k, v) => setF((s) => ({ ...s, [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v] }));

  const submit = async (e) => {
    e.preventDefault();
    const body = { name: f.name, percent: parseFloat(f.percent), starts_at: fromLocalInput(f.starts_at), ends_at: fromLocalInput(f.ends_at), days: f.days, start_time: f.start_time || null, end_time: f.end_time || null, channels: f.channels, active: f.active };
    setBusy(true);
    try {
      const { data } = initial?.id ? await adminApi(token).put(`/admin/promotions/${initial.id}`, body) : await adminApi(token).post("/admin/promotions", body);
      toast.success(initial?.id ? "Promotion updated" : `"${data.name}" created${data.status === "live" ? " — live now!" : ""}`);
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="promo-form">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-lg p-5 space-y-3 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="font-black text-stone-800 text-sm uppercase tracking-widest">{initial?.id ? "Edit promotion" : "New promotion"}</div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-stone-400" data-testid="promo-form-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-[1fr_110px] gap-3">
          <label className="block"><span className={label}>Name (customers see this)</span>
            <input value={f.name} onChange={set("name")} placeholder="e.g. Taco Tuesday" className={field} data-testid="promo-name" required autoFocus /></label>
          <label className="block"><span className={label}>% off order</span>
            <input value={f.percent} onChange={set("percent")} type="number" step="any" min="1" max="100" className={`${field} text-center font-black text-lg`} data-testid="promo-percent" required /></label>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block"><span className={label}>Starts (blank = right away)</span>
            <input value={f.starts_at} onChange={set("starts_at")} type="datetime-local" className={field} data-testid="promo-starts" /></label>
          <label className="block"><span className={label}>Ends (blank = until paused)</span>
            <input value={f.ends_at} onChange={set("ends_at")} type="datetime-local" className={field} data-testid="promo-ends" /></label>
        </div>
        <div>
          <span className={label}>Only on these days (none = every day)</span>
          <div className="flex flex-wrap gap-1.5 mt-1" data-testid="promo-days">
            {DAYS.map((d, i) => (
              <button key={d} type="button" onClick={() => toggle("days", i)} data-testid={`promo-day-${i}`}
                className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-black border ${f.days.includes(i) ? "bg-brand-red text-white border-brand-red" : "border-stone-200 text-stone-600"}`}>{d}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className={label}>Daily from (optional)</span>
            <input value={f.start_time} onChange={set("start_time")} type="time" className={field} data-testid="promo-start-time" /></label>
          <label className="block"><span className={label}>Daily until</span>
            <input value={f.end_time} onChange={set("end_time")} type="time" className={field} data-testid="promo-end-time" /></label>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <span className={label}>Applies to</span>
          {[["online", "Online orders"], ["pos", "POS / window"]].map(([k, t]) => (
            <label key={k} className="flex items-center gap-1.5 text-sm font-bold text-stone-700 cursor-pointer">
              <input type="checkbox" checked={f.channels.includes(k)} onChange={() => toggle("channels", k)} data-testid={`promo-channel-${k}`} className="w-4 h-4 accent-[#9B2226]" /> {t}
            </label>
          ))}
          <label className="flex items-center gap-1.5 text-sm font-bold text-stone-700 cursor-pointer ml-auto">
            <input type="checkbox" checked={f.active} onChange={(e) => setF((s) => ({ ...s, active: e.target.checked }))} data-testid="promo-active" className="w-4 h-4 accent-[#9B2226]" /> Active
          </label>
        </div>
        <button type="submit" disabled={busy} data-testid="promo-save" className="w-full bg-brand-red text-white py-3 rounded-full text-xs uppercase tracking-widest font-black disabled:opacity-50">{busy ? "Saving…" : initial?.id ? "Save changes" : "Create promotion"}</button>
      </form>
    </div>
  );
}

const when = (p) => {
  const parts = [];
  const f = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  if (p.starts_at || p.ends_at) parts.push(`${p.starts_at ? f(p.starts_at) : "now"} → ${p.ends_at ? f(p.ends_at) : "until paused"}`);
  if (p.days?.length) parts.push(p.days.map((d) => DAYS[d]).join(", "));
  if (p.start_time && p.end_time) parts.push(`${p.start_time}–${p.end_time}`);
  parts.push(p.channels.map((c) => (c === "pos" ? "POS" : "Online")).join(" + "));
  return parts.join(" · ");
};

export function PromotionsCard({ token }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => adminApi(token).get("/admin/promotions").then((r) => setList(r.data)).catch(() => {}), [token]);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (p) => {
    try {
      await adminApi(token).put(`/admin/promotions/${p.id}`, { ...p, active: !p.active });
      toast.success(!p.active ? "Promotion resumed" : "Promotion paused");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't update"); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await adminApi(token).delete(`/admin/promotions/${p.id}`).catch(() => toast.error("Couldn't delete"));
    load();
  };

  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="promotions-card">
      <div className="px-5 py-3 border-b border-stone-200 flex items-center gap-2">
        <BadgePercent className="w-4 h-4 text-brand-red" />
        <div className="font-black text-stone-800 text-sm uppercase tracking-widest">Site-wide promotions</div>
        <button type="button" onClick={() => setEditing({})} data-testid="promo-add-btn" className="ml-auto bg-brand-red text-white px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-black flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> New</button>
      </div>
      <div className="px-5 py-2 text-[11px] text-stone-500 border-b border-stone-100">A percentage off the <b>whole order</b>, applied automatically online and/or at the POS during the window you set. Customers see a banner on the storefront while it&apos;s live.</div>
      {list.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-stone-400" data-testid="promos-empty">No promotions yet — tap New to run something like 20% off all day Tuesday.</div>
      ) : (
        <ul className="divide-y divide-stone-100">
          {list.map((p) => {
            const st = STATUS[p.status] || STATUS.off;
            return (
              <li key={p.id} className="px-5 py-3 flex items-center gap-3" data-testid={`promo-row-${p.id}`}>
                <div className="w-14 h-14 rounded-2xl bg-stone-900 text-brand-yellow flex flex-col items-center justify-center shrink-0">
                  <span className="font-black text-lg leading-none">{Number(p.percent) % 1 === 0 ? p.percent : Number(p.percent).toFixed(1)}%</span>
                  <span className="text-[8px] uppercase tracking-widest">off</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-stone-800 flex items-center gap-2 flex-wrap">{p.name}<span className={`text-[9px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full ${st.cls}`} data-testid={`promo-status-${p.id}`}>{st.text}</span></div>
                  <div className="text-[11px] text-stone-500 truncate">{when(p)}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" onClick={() => toggleActive(p)} data-testid={`promo-toggle-${p.id}`} className={`text-[10px] uppercase tracking-widest font-black px-3 py-1.5 rounded-full border ${p.active ? "border-stone-200 text-stone-600" : "bg-emerald-600 text-white border-emerald-600"}`}>{p.active ? "Pause" : "Resume"}</button>
                  <button type="button" onClick={() => setEditing(p)} data-testid={`promo-edit-${p.id}`} className="p-1.5 rounded-lg text-stone-500 hover:text-brand-red hover:bg-red-50"><Pencil className="w-4 h-4" /></button>
                  <button type="button" onClick={() => remove(p)} data-testid={`promo-delete-${p.id}`} className="p-1.5 rounded-lg text-stone-400 hover:text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {editing && <PromoForm token={token} initial={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
